// FILE: src/components/Homepage.js

import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebase";
import {
  doc, getDoc, getDocs, query, where, collection,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { clearSessionBypass } from "../utils/mfa";
import VideoLightbox from "./ui/VideoLightbox";
import OnboardingChecklist from "./OnboardingChecklist";

function FreePlanBanner() {
  return (
    <div style={{
      background: "#fff0ee",
      border: "1px solid #f15822",
      borderRadius: 12,
      padding: "12px 20px",
      margin: "0 0 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 10,
    }}>
      <span style={{ fontSize: 14, color: "#650202", fontWeight: 600 }}>
        You’re on the free plan · Upgrade to unlock unlimited last wishes and full access
      </span>
      <a
        href="/personal/plans"
        style={{
          padding: "7px 16px",
          borderRadius: 8,
          background: "#980000",
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          textDecoration: "none",
          whiteSpace: "nowrap",
        }}
      >
        Upgrade &rarr;
      </a>
    </div>
  );
}

function PaymentFailedBanner({ user }) {
  const [loading, setLoading] = useState(false);

  async function openPortal() {
    setLoading(true);
    try {
      const cu = auth.currentUser;
      const token = cu ? await cu.getIdToken() : null;
      const customerId = user?.stripeCustomerId || null;
      if (!customerId) {
        window.location.assign("/profile");
        return;
      }
      const res = await fetch("/api/createCustomerPortal", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ customerId, returnUrl: window.location.href }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.assign(data.url);
      } else {
        window.location.assign("/profile");
      }
    } catch {
      window.location.assign("/profile");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 12,
      padding: "14px 18px", marginBottom: 16,
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 20 }}>⚠️</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#7f1d1d" }}>Payment failed</div>
        <div style={{ fontSize: 13, color: "#991b1b", marginTop: 2 }}>
          We could not process your last payment. Please update your payment details to keep your account active.
        </div>
      </div>
      <button onClick={openPortal} disabled={loading} style={{
        background: "#980000", color: "#fff", borderRadius: 8, border: "none",
        padding: "8px 14px", fontSize: 13, fontWeight: 700,
        cursor: loading ? "not-allowed" : "pointer", whiteSpace: "nowrap",
      }}>{loading ? "Loading..." : "Update payment"}</button>
    </div>
  );
}

/* ---------------- helpers ---------------- */

const asset = (p) =>
  p.startsWith("http") ? p : `${process.env.PUBLIC_URL}/${p.replace(/^\/+/, "")}`;

function fromStorage() {
  const keys = [
    "fullName",
    "profile.fullName",
    "userFullName",
    "displayName",
    "name",
  ];
  for (const k of keys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v && v.trim()) return v.trim();
  }
  return "";
}

async function resolveFullName(uid, fallback) {
  if (!uid) return fromStorage() || fallback;

  // 1) try common collections by doc id = uid
  const cols = [
    "profiles",
    "profile",
    "users",
    "userProfiles",
    "accounts",
    "settings",
    "userSettings",
  ];

  const tryRead = async (c) => {
    try {
      const snap = await getDoc(doc(db, c, uid));
      if (snap.exists()) return snap.data();
    } catch {}
    return null;
  };

  for (const c of cols) {
    const d = await tryRead(c);
    if (d) {
      const nm =
        d.fullName ||
        d.full_name ||
        d.name ||
        (d.firstName && d.lastName ? `${d.firstName} ${d.lastName}` : "") ||
        d.username ||
        d.displayName;
      if (nm && String(nm).trim()) return String(nm).trim();
    }
  }

  // 2) if not stored by uid key, try query by uid field
  const tryQuery = async (c) => {
    try {
      const q = query(collection(db, c), where("uid", "==", uid));
      const r = await getDocs(q);
      if (!r.empty) return r.docs[0].data();
    } catch {}
    return null;
  };

  for (const c of cols) {
    const d = await tryQuery(c);
    if (d) {
      const nm =
        d.fullName ||
        d.full_name ||
        d.name ||
        (d.firstName && d.lastName ? `${d.firstName} ${d.lastName}` : "") ||
        d.username ||
        d.displayName;
      if (nm && String(nm).trim()) return String(nm).trim();
    }
  }

  // 3) auth + storage fallbacks
  const authName =
    auth.currentUser?.displayName ||
    auth.currentUser?.providerData?.[0]?.displayName ||
    "";
  return (authName && authName.trim()) || fromStorage() || fallback;
}

/* ---------------- tiny inline icons ---------------- */
const _IconHome = (p) => (
  <svg viewBox="0 0 24 24" width="22" height="22" {...p}>
    <path fill="currentColor" d="M12 3 2 11h2v9h6v-6h4v6h6v-9h2L12 3z" />
  </svg>
);

const _IconUsers = (p) => (
  <svg viewBox="0 0 24 24" width="22" height="22" {...p}>
    <path fill="currentColor" d="M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4ZM8 12a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm8 2c-3 0-9 1.5-9 4.5V21h18v-2.5C25 15.5 19 14 16 14Zm-8 0c-.6 0-1.3.1-2 .2A7.2 7.2 0 0 1 1 19v2h7v-2.5a6.7 6.7 0 0 1 0-.5Z" />
  </svg>
);

const _IconUser = (p) => (
  <svg viewBox="0 0 24 24" width="22" height="22" {...p}>
    <path fill="currentColor" d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4 0-9 2-9 6v2h18v-2c0-4-5-6-9-6Z" />
  </svg>
);

const _IconLogout = (p) => (
  <svg viewBox="0 0 24 24" width="22" height="22" {...p}>
    <path fill="currentColor" d="M16 17v-3H9v-4h7V7l5 5-5 5ZM14 19H5V5h9V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9Z" />
  </svg>
);

export default function Homepage({ user }) {
  const fallback = useMemo(() => {
    if (user?.displayName?.trim()) return user.displayName.trim();
    if (user?.email) return user.email.split("@")[0];
    return "Friend";
  }, [user?.displayName, user?.email]);

  const [fullName, setFullName] = useState(fallback);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    let ok = true;
    (async () => {
      const nm = await resolveFullName(user?.uid, fallback);
      if (ok) setFullName(nm || fallback);
    })();
    return () => { ok = false; };
  }, [user?.uid, fallback]);

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const tenantId =
    user?.businessAccount?.tenantId ||
    user?.primaryTenantId ||
    user?.tenantId ||
    "";

  const tenantRole = String(
    user?.businessAccount?.role ||
    user?.tenantRole ||
    user?.role ||
    ""
  ).toLowerCase();

  const isBusinessOwner =
    tenantRole === "owner" ||
    tenantRole === "hr_admin";

  const hasBusinessAccount =
    !!tenantId &&
    ["owner", "hr_admin"].includes(tenantRole);

  const hasPersonalAccount =
    user?.personalAccount?.active === true ||
    user?.subscriptionStatus === "active" ||
    user?.subscriptionStatus === "free" ||
    user?.planKey === "free" ||
    user?.subscription?.status === "active" ||
    user?.subscription?.active === true;

  const tenantAdminHref =
    isBusinessOwner && tenantId
      ? `/tenant/${tenantId}`
      : "";

  const personalTiles = [
    { label: "Vaults", href: "/vaults/digital-platforms", img: asset("homeimages/Lastwishbox-Valuts-Vault.jpg") },
    { label: "Messages", href: "/messages/personal-messages", img: asset("homeimages/Lastwishbox-Messages-Personal-Messages.jpg") },
    { label: "Videos", href: "/messages/videos", img: asset("homeimages/Lastwishbox-Messages-video-messages.jpg") },
    { label: "Memories", href: "/personal/memory-lane", img: asset("homeimages/Lastwishbox-Memories.jpg") },
    { label: "Devices", href: "/vaults/devices", img: asset("homeimages/Lastwishbox-Valuts-Devices.jpg") },
    { label: "Documents", href: "/legal/important-documents", img: asset("homeimages/Lastwishbox-Important-Documents.jpg") },
  ];

  const businessTiles = [
    { label: "Contacts", href: "/contacts", img: asset("homeimages/Lastwishbox-Important-Documents.jpg") },
    { label: "Support", href: "/support", img: asset("homeimages/Lastwishbox-Messages-Personal-Messages.jpg") },
    { label: "Managed Accounts", href: "/account-managers/managed", img: asset("homeimages/Lastwishbox-Memories.jpg") },
  ];

  function _doLogout(e) {
    e.preventDefault();
    clearSessionBypass(auth.currentUser?.uid);
    signOut(auth).finally(() => window.location.assign("/login"));
  }

  // Mark “unused” vars as used without affecting UI (silences eslint)
  if (typeof window !== "undefined") {
    // why: your build flags these as unused; exposing in dev makes them “used” at lint-time
    window.__lw_home_keep = { _IconHome, _IconUsers, _IconUser, _IconLogout, _doLogout };
  }

  return (
    <div className="home-wrap">
      {/* Welcome */}
      <div className="home-head">
        <div className="home-welcome">
          Welcome <span className="welcome-name">{fullName}</span>
        </div>
        <div className="home-date">{today}</div>
      </div>

      <OnboardingChecklist
        user={auth.currentUser}
        tenantId={tenantId}
        tenantRole={tenantRole}
      />

      {(user?.subscriptionStatus === "free" || user?.planKey === "free") && (
        <FreePlanBanner />
      )}

      {(user?.paymentFailed || user?.subscriptionStatus === "past_due") && (
        <PaymentFailedBanner user={user} />
      )}

      {hasBusinessAccount ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e8ebf3",
            borderRadius: 16,
            padding: 18,
            boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
            marginBottom: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                color: "#2a0516",
                fontSize: 18,
                fontWeight: 800,
                marginBottom: 6,
              }}
            >
              Business Account
            </div>
            <div
              style={{
                color: "#5f6b7a",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {tenantAdminHref
                ? "Manage your tenant settings, billing, users, and plan from your business admin area."
                : "You have business access on this platform. Open your business workspace tools below."}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {tenantAdminHref ? (
              <a
                href={tenantAdminHref}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #2a0516",
                  background: "#2a0516",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontSize: 14,
                  fontWeight: 700,
                  textDecoration: "none",
                  boxShadow: "0 4px 14px rgba(42,5,22,0.12)",
                  whiteSpace: "nowrap",
                }}
              >
                Go to Tenant Admin
              </a>
            ) : null}

            <a
              href="/account-managers/managed"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#2a0516",
                borderRadius: 10,
                padding: "12px 16px",
                fontSize: 14,
                fontWeight: 700,
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              Managed Business Accounts
            </a>
          </div>
        </div>
      ) : null}

      {/* Hero */}
      <div className="home-hero">
        <img
          className="hero-img"
          src={asset("homeimages/Lastwishbox-Below-Beamer.jpg")}
          alt=""
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.parentElement.classList.add("hero-fallback");
          }}
        />
                    <button
          type="button"
          className="hero-overlay"
          onClick={() => setShowVideo(true)}
        >
          <div className="hero-overlay-text">See How It Works</div>
          <div className="hero-overlay-play" aria-hidden="true">
            <span className="hero-play-icon">▶</span>
          </div>
        </button>
      </div>

      {/* Orange lozenge paragraph */}
      <div className="home-pill" role="note">
        Keep your wishes, memories, and important information organised in one
        secure place. Share only with the people you trust - when it matters.
      </div>

      {hasPersonalAccount ? (
        <>
          <div className="home-subhead">
            Personal <span className="accent">Quick Links</span>
          </div>

          <div className="home-grid">
            {personalTiles.map((t) => (
              <a className="tile" key={t.label} href={t.href}>
                <img
                  className="tile-img"
                  src={t.img}
                  alt={t.label}
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                    e.currentTarget.parentElement.classList.add("tile-fallback");
                  }}
                />
                <div className="tile-label">{t.label}</div>
              </a>
            ))}
          </div>
        </>
      ) : null}

      {hasBusinessAccount ? (
        <>
          <div className="home-subhead" style={{ marginTop: hasPersonalAccount ? 18 : 10 }}>
            Business <span className="accent">Quick Links</span>
          </div>

          <div className="home-grid home-grid--business">
            {businessTiles.map((t) => (
              <a className="tile" key={t.label} href={t.href}>
                <img
                  className="tile-img"
                  src={t.img}
                  alt={t.label}
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                    e.currentTarget.parentElement.classList.add("tile-fallback");
                  }}
                />
                <div className="tile-label">{t.label}</div>
              </a>
            ))}
          </div>
        </>
      ) : null}

                <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/-VOP_Foutqc?si=OYNc0s7DvzE8NB6u"
        title="Welcome to Lastwishbox"
        startAt={0}
        captions={false}
        startMuted={false}
      />


      <style>{`
        :root{
          --ink:#2a0516;
          --muted:#657899;
          --orange:#f15822;
          --footer-h:64px;
          --radius:20px;
        }

        .home-wrap{
          max-width:1050px;
          margin:0 auto;
          padding: clamp(10px, 2.6vw, 20px);
        }

        .home-head{ margin-bottom: 8px; }
        .home-welcome{
          color:#111; font-weight:800;
          font-size: clamp(18px, 3.2vw, 28px);
          line-height:1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }
        .welcome-name{ color: var(--orange); }
        .home-date{ color: var(--muted); font-size: clamp(12px, 2.6vw, 14px); }

        .home-hero{
          border-radius: var(--radius);
          overflow: hidden;
          margin-top: 10px;
          background: #eef3f8;
          position: relative;
        }
        .hero-img{
          width:100%;
          height: clamp(120px, 18svh, 240px);
          object-fit: cover; display:block;
        }
        .hero-fallback{
          height: clamp(120px, 18svh, 240px);
          border-radius: var(--radius);
          background: linear-gradient(135deg,#eef1f7, #f8f9fb);
        }
        .hero-overlay{
          position:absolute;
          inset:0;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          gap:10px;
          border: none;
          background: linear-gradient(120deg,rgba(0,0,0,.32),rgba(0,0,0,.38));
          color:#fff;
          font-weight:800;
          font-size: clamp(13px, 2.8vw, 16px);
          letter-spacing:.2px;
          cursor:pointer;
          text-align:center;
          padding: 12px;
        }
        .hero-overlay:focus-visible{ outline: 3px solid #fff; outline-offset: -3px; }
        .hero-overlay-play{
          width: clamp(46px, 8vw, 72px);
          aspect-ratio:1;
          border-radius:999px;
          background: rgba(255,255,255,.94);
          display:grid;
          place-items:center;
          box-shadow: 0 6px 16px rgba(0,0,0,.35);
          transition: transform .15s ease;
        }
        .hero-play-icon{ color: var(--orange); font-size: clamp(20px, 4vw, 30px); margin-left: 2px; }
        .hero-overlay:hover .hero-overlay-play{ transform: scale(1.04); }

        .home-pill{
          margin: 10px auto 6px;
          background: var(--orange);
          color:#fff;
          border-radius: 12px;
          padding: 10px 16px;
          text-align:center;
          font-size: clamp(12px, 2.6vw, 14px);
          line-height: 1.2;
          max-width: 900px;
        }

        .home-subhead{
          margin: 10px 2px 6px;
          font-weight: 900;
          color:#111;
          font-size: clamp(16px, 3vw, 22px);
        }
        .home-subhead .accent{ color: var(--orange); }

        .home-grid{
          display:grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(10px, 2vw, 16px);
          margin-top: 4px;
        }
        .tile{
          text-decoration:none; color:inherit;
          display:flex; flex-direction:column; align-items:center; gap: 6px;
        }
        .tile-img{
          width:100%; aspect-ratio:1 / 0.7;
          border-radius: 16px; object-fit: cover; background:#e9edf4;
          display:block;
        }
        .tile-fallback{ background:#eef2f7; border-radius:22px; min-height: 100px; }
        .tile-label{ font-weight:800; color: var(--orange); font-size: clamp(12px, 2.6vw, 16px); }

        @media (max-width: 919px){
          .hero-img,.hero-fallback{ height: clamp(110px, 17svh, 190px); }
          .home-grid{ gap: 10px; }
        }
      `}</style>
    </div>
  );
}
